/**
 * Semantic-near-duplicate dedupe for correction prompts.
 *
 * Pure functions, no I/O, no AI calls. Deterministic.
 *
 * Two prompts are considered near-duplicates when their normalized text is
 * similar by either:
 *   - normalized Levenshtein ratio ≥ 0.7, OR
 *   - shared-token Jaccard ≥ 0.6
 *
 * The first hit wins — the candidate is folded into the existing group and
 * marked as a duplicate of the canonical entry.
 */
import { createHash } from "node:crypto";
import type { Correction } from "./corrections.js";

/** A correction entry augmented with dedupe metadata. */
export interface CorrectionEntry {
  /** Stable per-entry id (caller-provided or derived). */
  id: string;
  /** Original prompt text. */
  prompt: string;
  /** Numeric confidence (0..1) — used to pick canonical. Higher wins. */
  confidence: number;
  /** Source timestamp for tie-breaking. Earlier timestamps win on equal confidence. */
  ts: number;
  /** Original Correction (preserves signal, stack). */
  source: Correction;
  /** When this entry was folded into another group, the canonical id. */
  duplicateOf?: string;
}

/** Tokenize for Jaccard: lowercase, split on non-alphanumeric, drop empties + tiny tokens. */
function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/u)
    .filter((t) => t.length >= 2);
}

/** Normalize text for Levenshtein comparison: lowercase, collapse whitespace. */
function normalizeForSimilarity(text: string): string {
  return text.toLowerCase().replace(/\s+/g, " ").trim();
}

/** Iterative Levenshtein distance (O(min(a,b)) memory). */
function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  if (a.length > b.length) {
    [a, b] = [b, a];
  }

  const prev: number[] = new Array(a.length + 1);
  const curr: number[] = new Array(a.length + 1);
  for (let i = 0; i <= a.length; i++) prev[i] = i;

  for (let j = 1; j <= b.length; j++) {
    curr[0] = j;
    for (let i = 1; i <= a.length; i++) {
      const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
      curr[i] = Math.min(
        prev[i] + 1, // deletion
        curr[i - 1] + 1, // insertion
        prev[i - 1] + cost // substitution
      );
    }
    for (let i = 0; i <= a.length; i++) prev[i] = curr[i];
  }
  return prev[a.length];
}

/** Jaccard similarity over token sets: |A ∩ B| / |A ∪ B|. */
function jaccard(a: string, b: string): number {
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.length === 0 && tb.length === 0) return 1;
  if (ta.length === 0 || tb.length === 0) return 0;

  const setA = new Set(ta);
  const setB = new Set(tb);
  let intersection = 0;
  for (const t of setA) if (setB.has(t)) intersection++;
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/**
 * Compute text similarity in [0, 1]. Returns the max of:
 *   - normalized Levenshtein ratio  (1 - distance / maxLen)
 *   - Jaccard token overlap
 *
 * Empty-vs-empty returns 1. Empty-vs-nonempty returns 0.
 */
export function similarity(a: string, b: string): number {
  const na = normalizeForSimilarity(a);
  const nb = normalizeForSimilarity(b);
  if (!na && !nb) return 1;
  if (!na || !nb) return 0;

  const maxLen = Math.max(na.length, nb.length);
  const levRatio = 1 - levenshtein(na, nb) / maxLen;
  const jac = jaccard(a, b);
  return Math.max(levRatio, jac);
}

/** Dedup thresholds — exported for tests. */
export const SIMILARITY_THRESHOLD = 0.7;
export const JACCARD_THRESHOLD = 0.6;

/** Stable per-entry id derived from prompt + ts + index. */
function defaultId(prompt: string, ts: number, index: number): string {
  return createHash("sha1").update(`${ts}|${index}|${prompt}`).digest("hex").slice(0, 8);
}

/**
 * Wrap raw Corrections into CorrectionEntry[], assigning confidence + id.
 *
 * Default confidence heuristic: longer corrections are slightly less
 * trustworthy than short ones (more room for noise). Caps at 1.0, floors at 0.3.
 */
export function toCorrectionEntries(
  corrections: Correction[],
  opts: { confidence?: (c: Correction) => number } = {}
): CorrectionEntry[] {
  return corrections.map((c, i) => {
    const conf = opts.confidence
      ? Math.max(0, Math.min(1, opts.confidence(c)))
      : Math.max(0.3, Math.min(1, 1 - (c.prompt.length - 10) / 200));
    return {
      id: defaultId(c.prompt, c.ts, i),
      prompt: c.prompt,
      confidence: conf,
      ts: c.ts,
      source: c,
    };
  });
}

/**
 * Group near-duplicate corrections together. The highest-confidence entry
 * per group becomes the canonical; others get `duplicateOf` pointing at it.
 *
 * Iterates in input order — first entry in a group is the seed. If a later
 * entry is similar to any existing canonical, it folds into that group;
 * otherwise it becomes a new canonical seed.
 *
 * Deterministic: same input order + same text + same thresholds → same output.
 */
export function dedupeCorrections(corrections: CorrectionEntry[]): CorrectionEntry[] {
  type Group = { canonical: CorrectionEntry; members: CorrectionEntry[]; firstIndex: number };
  const groups: Group[] = [];

  corrections.forEach((entry, idx) => {
    let target: Group | null = null;
    for (const g of groups) {
      if (similarity(entry.prompt, g.canonical.prompt) >= SIMILARITY_THRESHOLD) {
        target = g;
        break;
      }
    }

    if (!target) {
      groups.push({
        canonical: { ...entry, duplicateOf: undefined },
        members: [],
        firstIndex: idx,
      });
      return;
    }

    // Decide canonical by confidence, then by earlier ts.
    const beatsCanonical =
      entry.confidence > target.canonical.confidence ||
      (entry.confidence === target.canonical.confidence && entry.ts < target.canonical.ts);

    if (beatsCanonical) {
      // Demote current canonical to a member pointing at the new one.
      target.members.push({ ...target.canonical, duplicateOf: entry.id });
      target.canonical = { ...entry, duplicateOf: undefined };
    } else {
      target.members.push(entry);
    }
  });

  // Re-emit in input order, preserving the original index of each group.
  // Within a group, the canonical comes first (it represents the group's
  // earliest position), followed by the members in the order they arrived.
  groups.sort((a, b) => a.firstIndex - b.firstIndex);

  const result: CorrectionEntry[] = [];
  for (const g of groups) {
    result.push({ ...g.canonical, duplicateOf: undefined });
    for (const m of g.members) {
      result.push({ ...m, duplicateOf: g.canonical.id });
    }
  }
  return result;
}